// Helpers to map daily entries → monthly ingredient aggregates with classwise split.
import { CLASS_CATEGORIES, QUARTERS, toNumber } from "./poshanConstants";
import { buildDayBreakdown, TARGET_COST } from "./mealPlan";
import { isHolidayDay, isNoMdmDay } from "./dayKind";
import { splitConsumed } from "./consumption";
import { isClasswiseRiceNorms } from "./riceNorms";

const FY_START_MONTH = { April: 3, May: 4, June: 5, July: 6, August: 7, September: 8, October: 9, November: 10, December: 11, January: 0, February: 1, March: 2 };

function monthFyYear(monthName, financialYear) {
  // financialYear like "2025-26" → starts April 2025, ends March 2026
  const [startStr] = (financialYear || "").split("-");
  const startYear = Number(startStr);
  if (!Number.isFinite(startYear)) return null;
  const m = FY_START_MONTH[monthName];
  if (m === undefined) return null;
  // Apr-Dec → startYear; Jan-Mar → startYear+1
  const year = m >= 3 ? startYear : startYear + 1;
  return { year, month: m };
}

export function quarterDateRange(financialYear, quarter) {
  const months = QUARTERS[quarter]?.months || [];
  if (!months.length) return null;
  const first = monthFyYear(months[0], financialYear);
  const last = monthFyYear(months[2], financialYear);
  if (!first || !last) return null;
  const fromDate = `${first.year}-${String(first.month + 1).padStart(2, "0")}-01`;
  // last day of last month
  const lastDate = new Date(last.year, last.month + 1, 0);
  const toDate = `${last.year}-${String(last.month + 1).padStart(2, "0")}-${String(lastDate.getDate()).padStart(2, "0")}`;
  return { fromDate, toDate };
}

function entryMonthName(dateStr) {
  // dateStr "yyyy-mm-dd"
  const [y, m] = dateStr.split("-").map(Number);
  if (!Number.isFinite(m)) return null;
  const idx = m - 1;
  return Object.keys(FY_START_MONTH).find((k) => FY_START_MONTH[k] === idx) || null;
}

/**
 * Aggregate daily entries into the months[] structure used by the Generator.
 * For each month in the quarter, compute ingredients[] (deduped by name) with
 * summed quantity and weighted rate. Also returns a `classwise` map per month
 * and per ingredient name → { balVatika, primary, upperPrimary } share of amount.
 */
export function aggregateDailyToMonthly(months, financialYear, dailyEntries) {
  // Build a per-month bucket of entries
  const buckets = new Map();
  const allByMonth = new Map(); // MDM + DR entries → expenditure / rice via the shared helper
  months.forEach((m) => { buckets.set(m.monthName, []); allByMonth.set(m.monthName, []); });
  dailyEntries.forEach((entry) => {
    const mn = entryMonthName(entry.date);
    if (mn && allByMonth.has(mn)) allByMonth.get(mn).push(entry);
    // Skip Dry-Ration days and Holiday markers — both are handled separately:
    //  • DR days drive the Dry-Ration Report (rice + cooking cost distributed
    //    to enrolled students in cash / kind, NOT cooked into a meal).
    //  • Holiday days carry no meal data at all.
    // Including either in the monthly ingredient / fund / rice aggregation
    // would pollute the Hand Receipts, Annexure-1 and Cooking Cost totals.
    if (entry.entryType === "dryration") return;
    // iter154 — skip BOTH holiday and working_no_mdm days. Previously we
    // only skipped holidays, so a working_no_mdm entry with zero
    // attendance would still fall through to the per-class tally and
    // show up as a 0-meal day on reports. Explicit skip is safer than
    // relying on attendance=0.
    if (isHolidayDay(entry) || isNoMdmDay(entry)) return;
    const monthName = entryMonthName(entry.date);
    if (monthName && buckets.has(monthName)) {
      buckets.get(monthName).push(entry);
    }
  });

  const derivedMonths = months.map((month) => {
    const bucket = buckets.get(month.monthName) || [];
    const ingMap = new Map(); // name → { quantity, amount, unit }
    const classwise = new Map(); // name → { balVatika, primary, upperPrimary, highSchool }
    let attendanceSum = { balVatika: 0, primary: 0, upperPrimary: 0, highSchool: 0, total: 0 };
    let totalMealsServed = 0;
    // Expenditure + rice = MDM + DR from the shared helper. Rice norm: switch OFF =
    // legacy shared-group rice on every day; ON = each class's own norm.
    const legacyRice = !isClasswiseRiceNorms();
    const split = splitConsumed(allByMonth.get(month.monthName) || [], { groupRice: legacyRice });
    const fundsByClass = split.total.cost;
    const riceByClass = split.total.rice;

    bucket.forEach((entry) => {
      const att = entry.attendance || {};
      const bv = toNumber(att.balVatika);
      const pr = toNumber(att.primary);
      const up = toNumber(att.upperPrimary);
      const hs = toNumber(att.highSchool);
      const total = bv + pr + up + hs;
      attendanceSum.balVatika += bv;
      attendanceSum.primary += pr;
      attendanceSum.upperPrimary += up;
      attendanceSum.highSchool += hs;
      attendanceSum.total += total;
      totalMealsServed += total;

      const breakdown = buildDayBreakdown(entry.date, att);

      // ----- INGREDIENT AGGREGATION -----
      // Use the deterministic per-day meal-plan breakdown as the source of
      // truth for monthly ingredient totals. Reading `entry.ingredients`
      // (saved via ingredientsForDailyEntry → qty.toFixed(3) × rate.toFixed(4))
      // loses precision and causes Annexure-1 grand total to drift from the
      // 3-month Total Cooking Cost. Sourcing directly from
      // `breakdown.aggregate` (whose totalAmount = Σ costPerStudent × count)
      // guarantees Annexure-1 grand total === Total Cooking Cost exactly.
      const dayShareBV = total ? bv / total : 0;
      const dayShareP = total ? pr / total : 0;
      const dayShareUP = total ? up / total : 0;
      (breakdown?.aggregate || []).forEach((row) => {
        if (!(row.totalAmount > 0) && !(row.totalQty > 0)) return;
        const qty = row.totalQty || 0;
        const amount = row.totalAmount || 0;
        const rate = qty > 0 ? amount / qty : 0;
        const cur = ingMap.get(row.name) || { name: row.name, unit: row.unit || "kg", quantity: 0, amount: 0, rateSum: 0, rateCount: 0 };
        cur.quantity += qty;
        cur.amount += amount;
        cur.rateSum += rate;
        cur.rateCount += 1;
        cur.unit = cur.unit || row.unit || "kg";
        ingMap.set(row.name, cur);

        // Classwise split per day: amount × class share of attendance.
        const cw = classwise.get(row.name) || { balVatika: 0, primary: 0, upperPrimary: 0, qtyBalVatika: 0, qtyPrimary: 0, qtyUpperPrimary: 0 };
        cw.balVatika += amount * dayShareBV;
        cw.primary += amount * dayShareP;
        cw.upperPrimary += amount * dayShareUP;
        cw.qtyBalVatika += qty * dayShareBV;
        cw.qtyPrimary += qty * dayShareP;
        cw.qtyUpperPrimary += qty * dayShareUP;
        classwise.set(row.name, cw);
      });
    });

    const ingredients = Array.from(ingMap.values()).map((row) => ({
      id: `daily-${row.name.toLowerCase().replace(/\s+/g, "-")}`,
      name: row.name,
      quantity: row.quantity,
      unit: row.unit,
      // Preserve EXACT summed amount (Σ qty_i × rate_i) so Annexure-1 grand
      // total = Fund-Management 3-month consumed. Without this the Vegetable
      // line (residual: rate varies day-to-day across egg/soya/dal days)
      // would mis-aggregate via qty_total × avg_rate.
      amount: Number((row.amount || 0).toFixed(2)),
      rate: row.rateCount ? row.rateSum / row.rateCount : 0,
      isCustom: false,
      isFromDaily: true,
    }));

    // If no daily entries for month, keep the manual ingredients[]
    const useDerived = bucket.length > 0;
    const hasConsumption = useDerived || split.drDays.length > 0;

    return {
      ...month,
      ingredients: useDerived ? ingredients : month.ingredients,
      _ingredientsFromDaily: useDerived,
      _classwiseFromDaily: useDerived ? classwise : null,
      _attendanceSum: useDerived ? attendanceSum : null,
      _dailyMealsServed: useDerived ? totalMealsServed : 0,
      _dailyEntryCount: bucket.length,
      _fundsByClassFromDaily: hasConsumption ? fundsByClass : null,
      _riceByClassFromDaily: hasConsumption ? riceByClass : null,
      _drFundsByClass: hasConsumption ? split.dr.cost : null,
      _drRiceByClass: hasConsumption ? split.dr.rice : null,
      _drDays: split.drDays.length,
    };
  });

  return derivedMonths;
}

export function emptyDailyDraft(schoolId, dateStr) {
  return {
    schoolId,
    date: dateStr,
    attendance: { balVatika: 0, primary: 0, upperPrimary: 0 },
    ingredients: [
      { id: `ing-${Date.now()}-1`, name: "", unit: "kg", quantity: 0, rate: 0 },
    ],
    notes: "",
  };
}
